import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Check, CreditCard, Spinner } from "@phosphor-icons/react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";

function PayPalPay({ planKey, planName, billingPeriod, onPaid }) {
  const periodRef = useRef(billingPeriod);
  periodRef.current = billingPeriod;
  return (
    <div className="mt-3" data-testid={`paypal-${planKey}`}>
      <PayPalButtons
        style={{ layout: "horizontal", color: "gold", shape: "pill", height: 40, tagline: false }}
        fundingSource="paypal"
        createOrder={async () => {
          const { data } = await api.post("/billing/paypal/create-order", { plan: planKey, billing_period: periodRef.current });
          return data.order_id;
        }}
        onApprove={async (data) => {
          try {
            const { data: res } = await api.post(`/billing/paypal/capture/${data.orderID}`);
            if (res.paid) { toast.success(`${planName} activated via PayPal`); onPaid(); }
            else toast.error("Payment not completed");
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        }}
        onError={() => toast.error("PayPal error. Please try again.")}
      />
      {billingPeriod === "annual" && <p className="text-[11px] text-zinc-500 text-center mt-1.5" data-testid={`paypal-annual-note-${planKey}`}>Billed once yearly</p>}
    </div>
  );
}

export default function Billing() {
  const { user, refreshCompany } = useAuth();
  const admin = isAdmin(user.role);
  const [plans, setPlans] = useState({});
  const [current, setCurrent] = useState("basic");
  const [txs, setTxs] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [paypal, setPaypal] = useState({ configured: false, client_id: "", loaded: false });

  const load = () => {
    api.get("/billing/plans").then(({ data }) => { setPlans(data.plans); setCurrent(data.current_plan); });
    api.get("/billing/transactions").then(({ data }) => setTxs(data)).catch(() => {});
    api.get("/billing/paypal/config").then(({ data }) => setPaypal({ ...data, loaded: true })).catch(() => setPaypal({ configured: false, loaded: true }));
  };
  useEffect(load, []);

  const subscribe = async (planKey) => {
    setLoadingPlan(planKey);
    try {
      const { data } = await api.post("/billing/checkout", { plan: planKey, origin_url: window.location.origin, billing_period: annual ? "annual" : "monthly" });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setLoadingPlan(null); }
  };

  const order = ["basic", "essential", "pro", "enterprise"];

  const grid = (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
      {order.map((key) => {
        const p = plans[key];
        if (!p) return null;
        const isCurrent = current === key;
        const popular = key === "pro";
        return (
          <div key={key} className={`p-6 rounded-xl border bg-white flex flex-col ${popular ? "border-emerald-500 ring-1 ring-emerald-500" : "border-zinc-200"}`} data-testid={`plan-card-${key}`}>
            {popular && <span className="self-start text-xs font-bold uppercase tracking-wider text-emerald-600 mb-2">Most popular</span>}
            <h3 className="font-display font-bold text-xl">{p.name}</h3>
            <div className="mt-3 mb-5">
              {p.custom ? (
                <><span className="text-zinc-500 text-sm font-semibold">from </span><span className="font-display text-4xl font-extrabold">${Math.round(p.base_price)}+</span><span className="text-zinc-500"> / mo</span></>
              ) : annual ? (
                <>
                  <span className="font-display text-4xl font-extrabold">${p.annual_base_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span><span className="text-zinc-500"> / yr</span>
                  {p.annual_price > 0 && <div className="text-sm text-zinc-500 mt-1">+ ${p.annual_price} per vehicle / yr</div>}
                  <div className="text-xs text-emerald-600 font-semibold mt-1">Save 15% vs ${(p.base_price * 12).toLocaleString(undefined, { minimumFractionDigits: 2 })}/yr</div>
                </>
              ) : (
                <>
                  <span className="font-display text-4xl font-extrabold">${p.base_price.toFixed(2)}</span><span className="text-zinc-500"> / mo</span>
                  {p.price > 0 && <div className="text-sm text-zinc-500 mt-1">+ ${p.price} per vehicle</div>}
                </>
              )}
            </div>
            <ul className="space-y-2.5 flex-1">
              {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-zinc-600"><Check size={18} weight="bold" className="text-emerald-500 shrink-0 mt-0.5" /> {f}</li>)}
            </ul>
            {p.custom ? (
              <a href="/get-demo" className="mt-6 block" data-testid={`contact-sales-${key}`}>
                <Button className="w-full h-12 font-semibold bg-zinc-950 hover:bg-zinc-800 text-white">Contact Sales</Button>
              </a>
            ) : isCurrent ? (
              <Button disabled className="mt-6 w-full h-12 font-semibold" variant="outline" data-testid={`current-${key}`}>Current Plan</Button>
            ) : admin ? (
              <div className="mt-6 space-y-2">
                <Button onClick={() => subscribe(key)} disabled={loadingPlan === key} className={`w-full h-12 font-semibold gap-2 ${popular ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "bg-zinc-950 hover:bg-zinc-800 text-white"}`} data-testid={`subscribe-${key}`}>
                  {loadingPlan === key ? <Spinner size={18} className="animate-spin" /> : <><CreditCard size={18} /> Pay with card</>}
                </Button>
                {paypal.configured ? (
                  <PayPalPay planKey={key} planName={p.name} billingPeriod={annual ? "annual" : "monthly"} onPaid={() => { load(); refreshCompany(); }} />
                ) : (
                  <p className="text-[11px] text-zinc-400 text-center pt-1" data-testid={`paypal-unconfigured-${key}`}>PayPal available once configured</p>
                )}
              </div>
            ) : (
              <Button disabled className="mt-6 w-full h-12" variant="outline">Admin only</Button>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div><h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Billing</h1><p className="text-zinc-500 text-sm mt-1">Manage your subscription. Currently on <span className="capitalize font-semibold text-emerald-600">{current}</span>.</p></div>

      <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="billing-notice">
        <strong>Live mode:</strong> Card payments are processed live through Stripe — real cards will be charged.
        {paypal.configured ? <> PayPal runs in <span className="font-semibold">{paypal.mode}</span> mode.</> : paypal.loaded ? <> PayPal is not configured yet — add your API credentials to enable it.</> : null}
      </div>

      {paypal.configured ? (
        <PayPalScriptProvider options={{ clientId: paypal.client_id, currency: "USD", intent: "capture" }}>
          <>
            <div className="flex justify-center"><div className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white p-1" data-testid="billing-period-toggle">
              <button onClick={() => setAnnual(false)} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${!annual ? "bg-zinc-950 text-white" : "text-zinc-600"}`} data-testid="billing-toggle-monthly">Monthly</button>
              <button onClick={() => setAnnual(true)} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${annual ? "bg-zinc-950 text-white" : "text-zinc-600"}`} data-testid="billing-toggle-annual">Annual <span className="text-[11px] font-bold text-emerald-500">Save 15%</span></button>
            </div></div>
            {grid}
          </>
        </PayPalScriptProvider>
      ) : (
        <>
          <div className="flex justify-center"><div className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white p-1" data-testid="billing-period-toggle">
            <button onClick={() => setAnnual(false)} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${!annual ? "bg-zinc-950 text-white" : "text-zinc-600"}`} data-testid="billing-toggle-monthly">Monthly</button>
            <button onClick={() => setAnnual(true)} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${annual ? "bg-zinc-950 text-white" : "text-zinc-600"}`} data-testid="billing-toggle-annual">Annual <span className="text-[11px] font-bold text-emerald-500">Save 15%</span></button>
          </div></div>
          {grid}
        </>
      )}

      <div>
        <h3 className="font-display font-bold text-lg mb-3">Billing History</h3>
        <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
          {txs.length === 0 ? <p className="text-center py-10 text-zinc-400 text-sm">No transactions yet.</p> : (
            <Table>
              <TableHeader><TableRow className="bg-zinc-50"><TableHead>Date</TableHead><TableHead>Plan</TableHead><TableHead>Method</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {txs.map((t) => (
                  <TableRow key={t.id} data-testid={`tx-${t.id}`}>
                    <TableCell className="text-sm">{new Date(t.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-sm capitalize">{t.plan}</TableCell>
                    <TableCell className="text-sm capitalize">{t.provider || "stripe"}</TableCell>
                    <TableCell className="text-sm">${t.amount} {t.currency?.toUpperCase()}</TableCell>
                    <TableCell><Badge variant="outline" className={`rounded-md ${t.payment_status === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-zinc-100 text-zinc-600"}`}>{t.payment_status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
